import type { ChatMessage, IntelligenceContext } from '../core/agents/types.js'
import type {
  CanonicalEvidenceV1,
  CanonicalSessionProjectionV1,
  CanonicalTranscriptEntryV1,
} from '../core/session/session-projection.js'
import {
  resolveCompanyNameForProjection,
} from './company-search-label.js'
import { resolveLeadFacingRole } from './identity-briefing-merge.js'

function isEmbeddedSessionSnapshot(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  return typeof record.sessionId === 'string'
}

export function flatContextRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      ([, candidate]) => !isEmbeddedSessionSnapshot(candidate)
    )
  )
}

export function canonicalTranscriptToChatMessages(
  transcript: CanonicalTranscriptEntryV1[]
): ChatMessage[] {
  return transcript
    .filter(entry => entry.state !== 'interim' && (entry.role === 'user' || entry.role === 'assistant'))
    .sort((left, right) => left.sequence - right.sequence)
    .map(entry => ({
      id: entry.entryId,
      timestamp: new Date(entry.occurredAt),
      role: entry.role as 'user' | 'assistant',
      content: entry.content,
    }))
}

export function canonicalProjectionToIntelligenceContext(
  projection: CanonicalSessionProjectionV1
): IntelligenceContext {
  const research = flatContextRecord(projection.researchContext)
  const researchPerson = flatContextRecord(research.person)
  const researchCompany = flatContextRecord(research.company)
  const state = projection.agentState
  const facts = Object.values(projection.sessionFacts).map(fact => fact.value)
  const visualObservations = projection.evidence
    .filter(
      evidence =>
        evidence.status === 'verified' &&
        (evidence.kind === 'screen' || evidence.kind === 'webcam'),
    )
    .map(evidence => ({
      modality: evidence.kind,
      analysis: evidence.summary,
      at: evidence.capturedAt,
      verified: true,
    }))

  // Never write `undefined` over researchContext fields — JS spread keeps the key and
  // wipes intake name/email that still live in researchContext when visitor slots are empty.
  const visitorName = projection.visitor.name?.trim() || undefined
  const visitorEmail = projection.visitor.email?.trim() || undefined
  const visitorRole = projection.visitor.role?.trim() || undefined
  const researchName =
    typeof research.name === 'string' && research.name.trim() ? research.name.trim() : undefined
  const researchEmail =
    typeof research.email === 'string' && research.email.trim() ? research.email.trim() : undefined
  const researchRole =
    typeof research.role === 'string' && research.role.trim() ? research.role.trim() : undefined
  const researchPersonRole =
    typeof researchPerson.role === 'string' && researchPerson.role.trim()
      ? researchPerson.role.trim()
      : undefined
  const resolvedRole = resolveLeadFacingRole({
    researchRole,
    personRole: researchPersonRole,
    verificationRole: visitorRole,
  })
  const rawCompanyName =
    projection.company.name?.trim() ||
    (typeof researchCompany.name === 'string' ? researchCompany.name.trim() : '') ||
    undefined
  const companyDomain =
    projection.company.domain?.trim() ||
    (typeof researchCompany.domain === 'string' ? researchCompany.domain.trim() : '') ||
    undefined
  // init-session's first company write has website "". Without a site the resolver swapped the
  // company the visitor typed for the email stem (N39 «Kari Berg (Farzadbayat)»). The bound
  // domain is the site it later writes, so read it that way until the real website lands.
  const companyWebsite =
    projection.company.website?.trim() || (companyDomain ? `https://${companyDomain}` : undefined)
  const companyName =
    resolveCompanyNameForProjection({
      researchName: rawCompanyName,
      emailDomain: companyDomain,
      companyUrl: companyWebsite,
    }) ?? companyDomain

  return {
    ...research,
    sessionId: projection.sessionId,
    ...(visitorName || researchName ? { name: visitorName || researchName } : {}),
    ...(visitorEmail || researchEmail ? { email: visitorEmail || researchEmail } : {}),
    ...(resolvedRole ? { role: resolvedRole } : {}),
    person: {
      ...researchPerson,
      ...(visitorName ? { fullName: visitorName } : {}),
      ...(resolvedRole ? { role: resolvedRole } : {}),
      ...(state.personSeniority ? { seniority: state.personSeniority } : {}),
    },
    company: {
      ...researchCompany,
      // Prefer canonical company; fall back to domain-as-label so agents never "forget" intake.
      ...(companyName
        ? { name: companyName }
        : companyDomain
          ? { name: companyDomain }
          : {}),
      ...(companyDomain ? { domain: companyDomain } : {}),
      ...(projection.company.website ? { website: projection.company.website } : {}),
      ...(projection.company.industry ? { industry: projection.company.industry } : {}),
      ...(projection.company.size ? { size: projection.company.size } : {}),
      ...(projection.company.summary ? { summary: projection.company.summary } : {}),
      ...(state.companyEmployeeCount !== null
        ? { employeeCount: state.companyEmployeeCount }
        : {}),
    },
    ...(projection.location.granted
      ? {
          location: {
            ...(projection.location.latitude !== null
              ? { latitude: projection.location.latitude }
              : {}),
            ...(projection.location.longitude !== null
              ? { longitude: projection.location.longitude }
              : {}),
            ...(projection.location.city ? { city: projection.location.city } : {}),
            ...(projection.location.country ? { country: projection.location.country } : {}),
          },
        }
      : {}),
    ...(facts.length > 0 ? { facts } : {}),
    ...(state.leadScore !== null ? { leadScore: state.leadScore } : {}),
    ...(state.fitScore.workshop !== null || state.fitScore.consulting !== null
      ? {
          fitScore: {
            workshop: state.fitScore.workshop ?? 0,
            consulting: state.fitScore.consulting ?? 0,
          },
        }
      : {}),
    ...(state.budget.hasExplicit ||
    state.budget.minUsd !== null ||
    state.budget.maxUsd !== null ||
    state.budget.urgency !== null
      ? {
          budget: {
            hasExplicit: state.budget.hasExplicit,
            urgency: state.budget.urgency ?? 0,
            ...(state.budget.minUsd !== null ? { minUsd: state.budget.minUsd } : {}),
            ...(state.budget.maxUsd !== null ? { maxUsd: state.budget.maxUsd } : {}),
          },
        }
      : {}),
    ...(state.timeline.urgency !== null || state.timeline.explicit
      ? {
          timeline: {
            urgency: state.timeline.urgency ?? 0,
            ...(state.timeline.explicit ? { explicit: state.timeline.explicit } : {}),
          },
        }
      : {}),
    ...(state.interestLevel !== null ? { interestLevel: state.interestLevel } : {}),
    ...(state.currentObjection ? { currentObjection: state.currentObjection } : {}),
    ...(state.researchConfidence !== null
      ? { researchConfidence: state.researchConfidence }
      : {}),
    ...(state.roleConfidence !== null ? { roleConfidence: state.roleConfidence } : {}),
    pitchDelivered: state.pitchDelivered,
    pitchTriggeredByUser: state.pitchTriggeredByUser,
    calendarBooked: state.calendarBooked,
    visualObservations,
    modalityStatus: {
      webcam: {
        active: projection.modalities.webcam.active,
        verifiedThisTurn: visualObservations.some(item => item.modality === 'webcam'),
      },
      screen: {
        active: projection.modalities.screen.active,
        verifiedThisTurn: visualObservations.some(item => item.modality === 'screen'),
      },
    },
    correctionHistory: projection.correctionHistory.map(correction => ({
      field: correction.key,
      oldValue: correction.previousValue ?? '',
      newValue: correction.correctedValue,
      turn: Math.max(0, projection.transcript.findIndex(entry => entry.turnId === correction.turnId)),
    })),
    lastUpdated: projection.updatedAt,
    identityConfirmed: projection.visitor.confirmedByUser,
  } as IntelligenceContext
}

export function canonicalProjectionToConversationFlow(
  projection: CanonicalSessionProjectionV1
): Record<string, unknown> {
  return {
    ...projection.conversationFlow,
    covered: { ...projection.conversationFlow.covered },
    evidence: { ...projection.conversationFlow.evidence },
    insights: { ...projection.conversationFlow.insights },
    coverageOrder: [...projection.conversationFlow.coverageOrder],
    ...(projection.conversationFlow.rejectedTopics
      ? { rejectedTopics: [...projection.conversationFlow.rejectedTopics] }
      : {}),
  }
}

function visualEvidenceToEntry(evidence: CanonicalEvidenceV1): Record<string, unknown> {
  const isScreen = evidence.kind === 'screen'
  return {
    id: evidence.evidenceId,
    timestamp: evidence.capturedAt,
    type: isScreen ? 'screen' : 'webcam',
    analysis: evidence.summary,
    imageData: '',
    metadata: {
      size: 0,
      format: isScreen ? 'screen' : 'webcam',
      confidence: evidence.confidence ?? 0,
      source: isScreen ? 'screenshare' : 'webcam',
      streamType: isScreen ? 'screen_share_stream' : 'webcam_stream',
      evidenceStatus: evidence.status,
      turnId: evidence.turnId,
    },
  }
}

export function canonicalProjectionToMultimodalContext(
  projection: CanonicalSessionProjectionV1
): Record<string, unknown> {
  const verified = projection.evidence.filter(evidence => evidence.status === 'verified')
  const visual = verified
    .filter(evidence => evidence.kind === 'screen' || evidence.kind === 'webcam')
    .map(visualEvidenceToEntry)
  const uploads = verified
    .filter(evidence => evidence.kind === 'file')
    .map(evidence => ({
      id: evidence.evidenceId,
      timestamp: evidence.capturedAt,
      filename: evidence.storageRef ?? evidence.evidenceId,
      mimeType: 'application/octet-stream',
      size: 0,
      analysis: evidence.summary,
      evidenceStatus: evidence.status,
      turnId: evidence.turnId,
    }))
  const audio = projection.transcript
    .filter(entry => entry.modality === 'voice' && entry.state !== 'interim')
    .map(entry => ({
      id: entry.entryId,
      type: 'voice_transcript',
      timestamp: entry.occurredAt,
      data: {
        transcript: entry.content,
        isFinal: true,
        turnId: entry.turnId,
      },
      metadata: { finalizedAt: entry.occurredAt },
    }))

  return {
    hasRecentImages: visual.length > 0,
    hasRecentAudio: audio.length > 0,
    hasRecentUploads: uploads.length > 0,
    recentAnalyses: visual.slice(-3).map(entry => String(entry.analysis)),
    recentUploads: uploads.slice(-5).map(entry => String(entry.analysis)),
    fullVisualContext: visual,
    fullAudioContext: audio,
    fullUploadContext: uploads,
    observations: verified,
  }
}
